/**
 * Guard Location Utilities
 *
 * Helper functions for transforming and processing guard location data.
 */

import { GuardLocation, GuardLocationRaw, GuardStatus } from './types';

/**
 * Determine guard status based on their data
 */
export function getGuardStatus(guard: GuardLocationRaw): GuardStatus {
  const { minutesAgo, currentPatrol } = guard;

  if (minutesAgo > 60) {
    return 'offline'; // No update in over an hour
  }

  if (currentPatrol) {
    if (minutesAgo <= 15) {
      return 'active'; // On patrol, recent update
    } else {
      return 'stale'; // On patrol, but update is getting old
    }
  }

  return 'not_on_patrol'; // Has location but not on patrol
}

/**
 * Format minutes ago into human-readable string
 */
export function formatLastSeen(minutesAgo: number): string {
  if (minutesAgo < 1) {
    return 'Just now';
  } else if (minutesAgo < 60) {
    return `${Math.round(minutesAgo)} min ago`;
  } else if (minutesAgo < 1440) {
    const hours = Math.floor(minutesAgo / 60);
    return `${hours} hr${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(minutesAgo / 1440);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
}

/**
 * Transform raw guard data to map-friendly format
 */
export function transformGuardData(guards: GuardLocationRaw[]): GuardLocation[] {
  return guards.map(guard => ({
    ...guard,
    status: getGuardStatus(guard),
    lastSeenFormatted: formatLastSeen(guard.minutesAgo),
    coordinates: { lat: guard.latitude, lng: guard.longitude },
  }));
}

/**
 * Filter guards based on filter state
 */
export function filterGuards(
  guards: GuardLocation[],
  filters: {
    showActive: boolean;
    showStale: boolean;
    showOffline: boolean;
    showNotOnPatrol: boolean;
    searchQuery: string;
  }
): GuardLocation[] {
  return guards.filter(guard => {
    // Status filters
    switch (guard.status) {
      case 'active':
        if (!filters.showActive) return false;
        break;
      case 'stale':
        if (!filters.showStale) return false;
        break;
      case 'offline':
        if (!filters.showOffline) return false;
        break;
      case 'not_on_patrol':
        if (!filters.showNotOnPatrol) return false;
        break;
    }

    // Search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const nameMatch = guard.name?.toLowerCase().includes(query);
      const customerMatch = guard.currentPatrol?.customerName?.toLowerCase().includes(query);
      const siteMatch = guard.currentPatrol?.siteName?.toLowerCase().includes(query);

      if (!nameMatch && !customerMatch && !siteMatch) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Sort guards based on sort option
 */
export function sortGuards(guards: GuardLocation[], sortBy: 'name' | 'lastSeen' | 'status'): GuardLocation[] {
  return [...guards].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'lastSeen':
        return a.minutesAgo - b.minutesAgo;
      case 'status':
        const statusOrder = { active: 0, stale: 1, not_on_patrol: 2, offline: 3 };
        return statusOrder[a.status] - statusOrder[b.status];
      default:
        return 0;
    }
  });
}

// ============================================================================
// Distance Calculation Utilities (for Patrol Circles)
// ============================================================================

/**
 * Calculate the distance between two geographic points using the Haversine formula.
 *
 * The Haversine formula determines the great-circle distance between two points
 * on a sphere given their longitudes and latitudes. This is more accurate than
 * simple Pythagorean distance for geographic coordinates.
 *
 * @param lat1 Latitude of first point in degrees
 * @param lng1 Longitude of first point in degrees
 * @param lat2 Latitude of second point in degrees
 * @param lng2 Longitude of second point in degrees
 * @returns Distance in meters
 */
export function calculateDistanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const EARTH_RADIUS_METERS = 6371000; // Earth's radius in meters

  // Convert degrees to radians
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const lat1Rad = toRadians(lat1);
  const lat2Rad = toRadians(lat2);
  const deltaLat = toRadians(lat2 - lat1);
  const deltaLng = toRadians(lng2 - lng1);

  // Haversine formula
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Check if a guard is within a given radius of a site location.
 *
 * @param guardLat Guard's latitude
 * @param guardLng Guard's longitude
 * @param siteLat Site center latitude
 * @param siteLng Site center longitude
 * @param radiusMeters Radius in meters (default: 50)
 * @returns true if guard is within radius
 */
export function isWithinRadius(
  guardLat: number,
  guardLng: number,
  siteLat: number,
  siteLng: number,
  radiusMeters: number = 50
): boolean {
  const distance = calculateDistanceMeters(guardLat, guardLng, siteLat, siteLng);
  return distance <= radiusMeters;
}

/**
 * Format distance in meters to a human-readable string.
 *
 * @param meters Distance in meters
 * @returns Formatted string (e.g., "25m", "1.2km")
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

/**
 * Formats a timestamp as relative time ("2m ago", "1h ago", etc.)
 * Moved here to avoid circular dependency between ActivityFeed and AlertsBar.
 * Timestamps from the API always include a Z suffix (JSON-serialized JS Dates).
 * Timestamps without timezone info are parsed as local time by the browser.
 */
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();

  if (diffMs < 0) return 'just now';

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  return `${diffDays}d ago`;
}
